import { t, getLocale, setLocale, type Locale } from './i18n.js';
import { MailAccess } from './MailAccess.js';
import { MailRules } from './MailRules.js';
import { AutoReply } from './AutoReply.js';
import { MyData } from './MyData.js';
import { useEffect, useState } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  Monitor,
  Smartphone,
  LogOut,
  Check,
  User,
  LockKeyhole,
  Filter,
  CalendarOff,
  Download,
} from 'lucide-react';
import { MAX_SIGNATURE_LENGTH, type Bootstrap, type SignaturePreview } from '../shared/types.js';
import { api, send } from './api.js';
import { Avatar, Loading, Notice, displayDate } from './components.js';
export function Settings({
  bootstrap,
  onUpdate,
  notify,
  onLogout,
  initialTab = 'profile',
}: {
  bootstrap: Bootstrap;
  onUpdate: () => void;
  notify: (s: string) => void;
  onLogout: () => void;
  initialTab?: string;
}) {
  const [tab, setTab] = useState(initialTab),
    [name, setName] = useState(bootstrap.user.name),
    [signature, setSignature] = useState(bootstrap.user.signature),
    [preview, setPreview] = useState<SignaturePreview & { source: string }>({
      source: bootstrap.user.signature,
      html: bootstrap.user.signatureHtml,
      text: bootstrap.user.signatureText,
    }),
    [previewError, setPreviewError] = useState(''),
    [sessions, setSessions] = useState<any[]>(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const load = () =>
    api('/sessions')
      .then(setSessions)
      .catch((e) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);
  useEffect(() => {
    if (signature === preview.source) return;
    let canceled = false;
    setPreviewError('');
    const timer = setTimeout(() => {
      void send<SignaturePreview>('/profile/signature-preview', { signature })
        .then((result) => {
          if (!canceled) setPreview({ ...result, source: signature });
        })
        .catch((e) => {
          if (!canceled) setPreviewError(e.message);
        });
    }, 300);
    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [signature, preview.source]);
  return (
    <main className="workspace-page settings-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">{t('COMME VOUS L’AIMEZ')}</span>
          <h1>{t('Votre espace, vos préférences.')}</h1>
          <p>{t('Personnalisez vos messages et gardez le contrôle de votre accès.')}</p>
        </div>
      </header>
      <section className="admin-card">
        <div className="admin-tabs">
          <button className={tab === 'profile' ? 'active' : ''} onClick={() => setTab('profile')}>
            <User size={17} />
            {t('Mon profil')}
          </button>
          <button className={tab === 'rules' ? 'active' : ''} onClick={() => setTab('rules')}>
            <Filter size={17} />
            {t('Règles de tri')}
          </button>
          <button className={tab === 'absence' ? 'active' : ''} onClick={() => setTab('absence')}>
            <CalendarOff size={17} />
            {t('Absence')}
          </button>
          <button className={tab === 'data' ? 'active' : ''} onClick={() => setTab('data')}>
            <Download size={17} />
            {t('Mes données')}
          </button>
          <button className={tab === 'security' ? 'active' : ''} onClick={() => setTab('security')}>
            <LockKeyhole size={17} />
            {t('Sécurité et sessions')}
          </button>
        </div>
        {error && <Notice type="error">{error}</Notice>}
        {tab === 'rules' ? (
          <MailRules notify={notify} remote={bootstrap.mailBackend === 'imap'} />
        ) : tab === 'data' ? (
          <MyData />
        ) : tab === 'absence' ? (
          <AutoReply notify={notify} remote={bootstrap.mailBackend === 'imap'} onSaved={onUpdate} />
        ) : tab === 'profile' ? (
          <form
            className="settings-form stack-form"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError('');
              try {
                await send('/profile', { name, signature }, 'PATCH');
                onUpdate();
                notify(t('Vos préférences sont enregistrées.'));
              } catch (e: any) {
                setError(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <div className="profile-header">
              <Avatar name={name} size="large" />
              <div>
                <h2>{bootstrap.user.name}</h2>
                <p>{bootstrap.mailbox?.address ?? bootstrap.user.email}</p>
              </div>
            </div>
            <label>
              {t('Nom affiché')}
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                maxLength={120}
              />
            </label>
            <label>
              {t('Signature personnelle')}
              <textarea
                rows={5}
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                maxLength={MAX_SIGNATURE_LENGTH}
                aria-describedby="signature-help"
              />
              <small id="signature-help">
                {t(
                  t(
                    'Collez du texte ou du code HTML. La signature est ajoutée aux nouveaux messages et réponses.',
                  ),
                )}
              </small>
            </label>
            <section
              className="signature-preview"
              aria-label={t('Aperçu de la signature')}
              aria-busy={signature !== preview.source && !previewError}
            >
              <h3>{t('Aperçu de la signature')}</h3>
              {previewError ? (
                <Notice type="error">{previewError}</Notice>
              ) : signature !== preview.source ? (
                <Loading label={t('Préparation de l’aperçu…')} />
              ) : !signature.trim() ? (
                <p className="muted">{t('Aucune signature pour le moment.')}</p>
              ) : (
                <iframe
                  title={t('Signature personnelle — aperçu')}
                  sandbox=""
                  referrerPolicy="no-referrer"
                  srcDoc={`<!doctype html><html lang="fr"><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src 'none'; base-uri 'none'; form-action 'none'"><style>body{font:14px/1.6 Arial,sans-serif;color:#272332;margin:0;overflow-wrap:anywhere}table{max-width:100%}</style><body>${preview.html}</body></html>`}
                />
              )}
              <small>
                {t(
                  t(
                    'La mise en forme est conservée. Les images distantes et contenus actifs sont bloqués.',
                  ),
                )}
              </small>
            </section>
            <div className="preferences-row">
              <div>
                <strong>{t('Langue de l’interface')}</strong>
                <small>{t('Navigation, messagerie, agenda et paramètres.')}</small>
              </div>
              <select
                aria-label={t('Langue de l’interface')}
                value={getLocale()}
                onChange={(e) => {
                  const locale = e.target.value as Locale;
                  setLocale(locale);
                  void send('/profile', { name, signature, locale }, 'PATCH')
                    .then(() => {
                      onUpdate();
                      notify(t('Vos préférences sont enregistrées.'));
                    })
                    .catch((err) => setError(err.message));
                }}
              >
                <option value="fr">{t('Français')}</option>
                <option value="en">{t('Anglais')}</option>
              </select>
            </div>
            <div className="preferences-row">
              <div>
                <strong>{t('Images distantes')}</strong>
                <small>
                  {t('Bloquées pour protéger votre confidentialité et économiser les données.')}
                </small>
              </div>
              <ShieldCheck size={20} />
            </div>
            <button className="primary" disabled={busy}>
              <Check size={17} />
              {busy ? t('Enregistrement…') : t('Enregistrer les modifications')}
            </button>
          </form>
        ) : (
          <div className="settings-form">
            {bootstrap.user.mfaEnabled || !bootstrap.oidcEnabled ? (
              <div className="security-banner">
                <span>
                  <ShieldCheck size={28} />
                </span>
                <div>
                  <h2>{t('Votre compte est protégé.')}</h2>
                  <p>{t('La double authentification est activée.')}</p>
                </div>
                {bootstrap.oidcEnabled ? (
                  // Keycloak replaces the recovery codes after a new sign-in (account action).
                  <a
                    className="secondary small"
                    href={`/v1/auth/oidc?action=recovery&locale=${getLocale()}`}
                  >
                    {t('Nouveaux codes de secours')}
                  </a>
                ) : (
                  <span className="badge green">{t('MFA activé')}</span>
                )}
              </div>
            ) : (
              <div className="security-banner optional">
                <span>
                  <ShieldAlert size={28} />
                </span>
                <div>
                  <h2>{t('Renforcez la protection de votre compte.')}</h2>
                  <p>
                    {t(
                      'Avec la double authentification, un code de votre application d’authentification est demandé en plus du mot de passe à chaque connexion.',
                    )}
                  </p>
                </div>
                <a
                  className="primary small"
                  href={`/v1/auth/oidc?action=totp&locale=${getLocale()}`}
                >
                  {t('Activer la double authentification')}
                </a>
              </div>
            )}
            {bootstrap.mailApplications && <MailAccess notify={notify} />}
            {bootstrap.proCollaboration && bootstrap.oidcEnabled && (
              <div className="pro-card">
                <h3>{t('Connexion Microsoft de votre organisation')}</h3>
                <p>
                  {t(
                    'Si votre administrateur a activé Microsoft Entra ID, liez votre identité Microsoft à ce compte Vename Mail depuis cette session protégée.',
                  )}
                </p>
                <a className="secondary" href="/v1/auth/entra/link">
                  {t('Lier mon compte Microsoft')}
                </a>
              </div>
            )}
            <h3>{t('Vos sessions ouvertes')}</h3>
            <p className="muted">
              {t('Vous pouvez fermer l’accès d’un appareil que vous ne reconnaissez pas.')}
            </p>
            {!sessions ? (
              <Loading />
            ) : (
              <div className="session-list">
                {sessions.map((s) => (
                  <div className="session-row" key={s.id}>
                    {/Mobile|Android|iPhone/i.test(s.userAgent) ? (
                      <Smartphone size={24} />
                    ) : (
                      <Monitor size={24} />
                    )}
                    <div>
                      <strong>
                        {/Mobile|Android|iPhone/i.test(s.userAgent)
                          ? t('Appareil mobile')
                          : t('Navigateur web')}{' '}
                        {s.current && <span className="badge green">{t('Cet appareil')}</span>}
                      </strong>
                      <small>
                        {t('Dernière activité : {date}', { date: displayDate(s.lastSeen, true) })}
                      </small>
                      <small>{t('Adresse IP : {ip}', { ip: s.ip })}</small>
                    </div>
                    {!s.current && (
                      <button
                        className="secondary small"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await api(`/sessions/${s.id}`, { method: 'DELETE' });
                            await load();
                            notify(t('Session révoquée.'));
                          } catch (e: any) {
                            setError(e.message);
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        {t('Révoquer')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <button className="secondary" onClick={onLogout}>
              <LogOut size={17} />
              {t('Se déconnecter de cet appareil')}
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
